import { isEqual, max, omit, values, first } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { ThemeProvider } from 'styled-components';
import Zocdoc from '@zocdoc/branding/brands/zocdoc/client';

import { withEvents } from '../../mixins';
import parseDoctorLocationIds from './utils/parseDoctorLocationIds';
import parseApiV1Response from '../api/utils/v1/parseApiV1Response';
import { Dispatcher } from '../utils/flux';
import ServerRequestActions from '../actions/ServerRequestActions';
import ServerResponseActions from '../actions/ServerResponseActions';
import TimesGridActions from '../actions/TimesGridActions';
import ExternalActions from '../actions/ExternalActions';
import TimesApi from '../api/TimesApi';
import AppointmentTimesStore from '../stores/AppointmentTimesStore';
import TimesGridStateStore from '../stores/TimesGridStateStore';
import TimesGridState from '../models/TimesGridState';
import AppEventCoordinator from '../events/AppEventCoordinator';
import * as Constants from '../constants/Constants';
import * as TimesGridStateStoreEvents from '../constants/TimesGridStateStoreEvents';
import getDateRange from '../utils/getDateRange';
import filterToDatesWithoutData from './utils/filterToDatesWithoutData';
import { SIZE_TO_NUM_COLS } from '../constants/ViewConstants';

const numDaysToFetchData = max(values(SIZE_TO_NUM_COLS));

export function createTimesGridStores(props) {
    const doctorLocationIds = parseDoctorLocationIds(props.doctorLocationIds);

    // Initialize the dispatcher
    const dispatcher = new Dispatcher();


    // Initialize the API and associated action creators
    const responseActions = new ServerResponseActions({
        dispatcher,
    });

    const api = new TimesApi({
        responseActions,
    });

    const requestActions = new ServerRequestActions({
        dispatcher,
        api,
    });


    // Initialize other action creators
    const gridActions = new TimesGridActions({
        dispatcher,
    });

    const externalActions = new ExternalActions({
        dispatcher,
    });

    // Initialize the stores
    const newPatientTimesStore = new AppointmentTimesStore({
        dispatcher,
        doctorLocationIds,
        initialTimes: parseApiV1Response(props.bootstrappedNewPatientTimes),
        isExistingPatient: false,
    });

    const existingPatientTimesStore = new AppointmentTimesStore({
        dispatcher,
        doctorLocationIds,
        initialTimes: parseApiV1Response(props.bootstrappedExistingPatientTimes),
        isExistingPatient: true,
    });

    const gridState = new TimesGridState(doctorLocationIds, props);
    const gridStateStore = new TimesGridStateStore({
        dispatcher,
        doctorLocationIds,
        existingPatientTimesStore,
        newPatientTimesStore,
        state: gridState,
    });


    // Initialize the event coordinator
    const eventCoordinator = new AppEventCoordinator({
        dispatcher,
        gridStateStore,
        gridActions,
    });

    return {
        doctorLocationIds,
        dispatcher,
        responseActions,
        api,
        requestActions,
        gridActions,
        externalActions,
        gridState,
        gridStateStore,
        eventCoordinator,
    };
}

export default function wrappedTimesGrid(WrappedTimesGridApp) {
    class TimesGridApp extends Component {
        static propTypes = {
            // Required Parameters
            doctorLocationIds: PropTypes.oneOfType([
                PropTypes.arrayOf(PropTypes.string),
                PropTypes.string,
            ]).isRequired,

            // Optional Parameters
            apikey: PropTypes.string,
            availabilityTimeFilter: PropTypes.number,
            bootstrappedExistingPatientTimes: PropTypes.object,
            bootstrappedNewPatientTimes: PropTypes.object,
            date: PropTypes.instanceOf(moment),
            directoryId: PropTypes.string,
            insuranceCarrierId: PropTypes.string,
            insurancePlanId: PropTypes.string,
            isExistingPatient: PropTypes.bool,
            isMobile: PropTypes.bool,
            isReschedule: PropTypes.bool,
            onClickMore: PropTypes.func,
            onClickNext: PropTypes.func,
            onClickNextAvailable: PropTypes.func,
            onClickPrev: PropTypes.func,
            onTimeSlotClicked: PropTypes.func,
            procedureId: PropTypes.string,
            referrerType: PropTypes.string,
            shouldJumpToFirstAppointment: PropTypes.bool,
            widget: PropTypes.string,
        };

        static defaultProps = {
            apikey: '',
            isMobile: false,
        };

        static getDerivedStateFromProps(nextProps, prevState) {
            /**
             * Basically if props.isMobile changes we want to take that as a higher priority
             * over what's in the flux store, so we cache what props.isMobile was before in
             * state for comparison.
             *
             * state.isMobile can change independently of the props due to the code listening
             * to the flux store state change (see componentDidMount.)
             */
            if (nextProps.isMobile !== prevState.cachedIsMobile) {
                return {
                    cachedIsMobile: nextProps.isMobile,
                    isMobile: nextProps.isMobile,
                };
            }

            return null;
        }

        constructor(props, ...args) {
            super(props, ...args);

            this.state = {
                // eslint-disable-next-line react/no-unused-state
                cachedIsMobile: props.isMobile,
                isMobile: props.isMobile,
            };

            this.stores = props.stores || createTimesGridStores(props);
        }

        componentDidMount() {
            this.propagateEvents();

            this.listenTo(this.stores.gridStateStore, [
                TimesGridStateStoreEvents.CHANGE_AVAILABILITY_TIME_FILTER,
                TimesGridStateStoreEvents.CHANGE_DATE,
                TimesGridStateStoreEvents.CHANGE_GRID_TYPE,
                TimesGridStateStoreEvents.CHANGE_INSURANCE,
                TimesGridStateStoreEvents.CHANGE_PROCEDURE,
                TimesGridStateStoreEvents.CHANGE_IS_EXISTING_PATIENT,
                TimesGridStateStoreEvents.CHANGE_DOCTOR_LOCATION_IDS,
            ].join(' '), this.onParametersChange);

            // since we aren't storing references to things in state, a forceUpdate is needed
            // to cause a rerender, which will pick up on the updated isMobile value
            this.listenTo(this.stores.gridStateStore, TimesGridStateStoreEvents.CHANGE_GRID_TYPE, () => {
                this.setState({
                    isMobile: this.stores.gridStateStore.getState().gridType === Constants.TYPE.MOBILE,
                });
            });

            if (!this.stores.gridStateStore.getState().initialized) {
                this.ensureTimesAreLoaded() // kick off request to fetch data before render
                    .then(() => this.maybeShowFirstAppointment()); // jump to the first appointment
            }
        }

        componentDidUpdate(prevProps) {
            if (this.props.isExistingPatient !== prevProps.isExistingPatient) {
                this.changeIsExistingPatient(this.props.isExistingPatient);
            }

            if (this.props.isMobile !== prevProps.isMobile) {
                this.changeGridType(this.props.isMobile);
            }

            if (!isEqual(this.props.doctorLocationIds, prevProps.doctorLocationIds)) {
                this.changeDoctorLocationIds(this.props.doctorLocationIds);
            }

            if (this.props.procedureId !== prevProps.procedureId) {
                this.changeProcedure(this.props.procedureId);
            }

            if (this.props.insuranceCarrierId !== prevProps.insuranceCarrierId
                || this.props.insurancePlanId !== prevProps.insurancePlanId
            ) {
                this.changeInsurance(this.props.insuranceCarrierId, this.props.insurancePlanId);
            }
        }

        componentWillUnmount() {
            this.stopListening();
        }

        render() {
            const props = {
                ...(omit(this.props, 'styles')),
                gridActions: this.stores.gridActions,
                gridStateStore: this.stores.gridStateStore,
                timesStore: this.stores.gridStateStore.timesStore,
            };

            return (
                <ThemeProvider theme={theme => theme || Zocdoc}>
                    <WrappedTimesGridApp
                        {...props}
                        isMobile={this.state.isMobile}
                    />
                </ThemeProvider>
            );
        }

        ensureTimesAreLoaded() {
            const gridState = this.stores.gridStateStore.getState();
            const dates = getDateRange(this.stores.gridState.date, numDaysToFetchData);

            const filteredDates =
                filterToDatesWithoutData(this.stores.doctorLocationIds, dates, this.stores.gridStateStore.timesStore);

            return this.stores.requestActions.fetchTimes(gridState, filteredDates);
        }

        onParametersChange() {
            this.ensureTimesAreLoaded();
        }

        propagateEvents() {
            this.listenTo(this.stores.eventCoordinator, 'all', (...args) => {
                this.trigger(...args);
            });
        }

        maybeShowFirstAppointment() {
            if (this.props.shouldJumpToFirstAppointment) {
                // note that although doctorLocationIds is an array, only the current location in focus is passed
                const currentLocation = this.props.doctorLocationIds[0];
                const times = this.stores.gridStateStore.timesStore.getByDoctorLocationOnly(currentLocation);
                const firstTime = first(values(times));
                if (firstTime && firstTime.nextAppointment) {
                    this.stores.gridActions.jumpToDate(firstTime.nextAppointment);
                }
            }
        }

        // PUBLIC METHODS - START

        /**
         * Updates the availability filter for the appointment times.
         *
         * @param {int} availabilityTimeFilter
         */
        changeAvailabilityTimeFilter(availabilityTimeFilter) {
            this.stores.externalActions.changeAvailabilityTimeFilter(availabilityTimeFilter);
        }

        /**
         * Updates the selected insurance for the appointment times.
         *
         * @param {int} insuranceCarrier
         * @param {int} insurancePlan
         */
        changeInsurance(insuranceCarrier, insurancePlan) {
            this.stores.externalActions.changeInsurance(insuranceCarrier, insurancePlan);
        }

        /**
         * Updates the procedure ID for the appointment times.
         *
         * @param {string} procedureId
         */
        changeProcedure(procedureId) {
            this.stores.externalActions.changeProcedure(procedureId);
        }

        /**
         * Updates whether the user is an existing patient.
         *
         * @param isExisting
         */
        changeIsExistingPatient(isExisting) {
            this.stores.externalActions.changeIsExistingPatient(isExisting);
        }

        changeDoctorLocationIds(doctorLocationIds) {
            const updatedDoctorLocationIds = parseDoctorLocationIds(doctorLocationIds);
            this.stores.externalActions.changeDoctorLocationIds(updatedDoctorLocationIds);
        }

        changeGridType(isMobile) {
            this.stores.gridActions.changeGridType(isMobile);
        }

        // PUBLIC METHODS - END
    }

    return withEvents(TimesGridApp);
}
